import { word } from "@modules/core/utils/i18n"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import {
  TransactionDto,
  TransactionProductDto,
  TransactionProductType,
  TransactionUserDto,
} from "@modules/finances/gateway/transactionDto"
import { PaymentMethodType, TransactionStatus } from "@modules/finances/types/financesType"
import { HUImage } from "@modules/types/imageTypes"
import { colors } from "@modules/ui/styles/colors"
import { DefaultTheme } from "styled-components"

export class TransactionUserEntity {
  id: string
  firstname: string
  lastname: string
  username: string
  avatar: HUImage | null

  private constructor(transactionUser: TransactionUserEntity) {
    this.id = transactionUser.id
    this.firstname = transactionUser.firstname
    this.lastname = transactionUser.lastname
    this.username = transactionUser.username
    this.avatar = transactionUser.avatar
  }

  static fromDto(transactionUserDto: TransactionUserDto): TransactionUserEntity {
    return new TransactionUserEntity({
      id: transactionUserDto.id,
      firstname: transactionUserDto.firstname,
      lastname: transactionUserDto.lastname,
      username: transactionUserDto.username,
      avatar: transactionUserDto.avatar ? convertImageFromDto(transactionUserDto.avatar) : null,
    })
  }
}

export class TransactionProductEntity {
  id: string
  name: string
  type: TransactionProductType

  private constructor(transactionProduct: TransactionProductEntity) {
    this.id = transactionProduct.id
    this.name = transactionProduct.name
    this.type = transactionProduct.type
  }

  static fromDto(transactionProductDto: TransactionProductDto): TransactionProductEntity {
    return new TransactionProductEntity({
      id: transactionProductDto.id,
      name: transactionProductDto.name,
      type: transactionProductDto.type,
    })
  }
}

type TransactionEntityState = Omit<TransactionEntity, "getTransactionColorStatus" | "getTransactionStatusWording">
export class TransactionEntity {
  transactionId: string
  date: Date
  user: TransactionUserEntity
  amount: number
  fee: number
  netAmount: number
  currency: string
  status: TransactionStatus
  disputed: boolean
  refunded: boolean
  product: TransactionProductEntity
  paymentMethodType: PaymentMethodType

  private constructor(transaction: TransactionEntityState) {
    this.transactionId = transaction.transactionId
    this.date = transaction.date
    this.user = transaction.user
    this.amount = transaction.amount
    this.fee = transaction.fee
    this.netAmount = transaction.netAmount
    this.currency = transaction.currency
    this.status = transaction.status
    this.disputed = transaction.disputed
    this.refunded = transaction.refunded
    this.product = transaction.product
    this.paymentMethodType = transaction.paymentMethodType
  }

  static fromDto(transactionDto: TransactionDto): TransactionEntity {
    return new TransactionEntity({
      transactionId: transactionDto.transactionId,
      date: new Date(transactionDto.date),
      user: TransactionUserEntity.fromDto(transactionDto.user),
      amount: transactionDto.amount,
      fee: transactionDto.fee,
      netAmount: transactionDto.netAmount,
      currency: transactionDto.currency,
      status: transactionDto.status,
      disputed: transactionDto.disputed,
      refunded: transactionDto.refunded,
      product: TransactionProductEntity.fromDto(transactionDto.product),
      paymentMethodType: transactionDto.paymentMethodType,
    })
  }

  getTransactionColorStatus(theme: DefaultTheme) {
    switch (this.status) {
      case TransactionStatus.SUCCEEDED:
        return colors.green500
      case TransactionStatus.PENDING:
        return this.paymentMethodType === PaymentMethodType.SEPA_DEBIT ? colors.green500 : colors.yellow500
      case TransactionStatus.FAILED:
        return colors.red500
      case TransactionStatus.DISPUTED:
        return colors.red500
      case TransactionStatus.REFUNDED:
        return theme.blackAtheleWhiteOwner
    }
  }

  getTransactionStatusWording() {
    const wordStatus: { [key in TransactionStatus]: string | undefined } = {
      SUCCEEDED: word("finances.page.table.status.succeded", { count: 1 }),
      PENDING: word("finances.page.table.status.pending", { count: 1 }),
      FAILED: word("finances.page.table.status.failed", { count: 1 }),
      DISPUTED: word("finances.page.table.status.disputed", { count: 1 }),
      REFUNDED: word("finances.page.table.status.refunded", { count: 1 }),
    }

    if (this.refunded) return word("finances.page.table.status.refunded", { count: 1 })
    if (this.status === TransactionStatus.PENDING)
      if (this.paymentMethodType === PaymentMethodType.SEPA_DEBIT) {
        return word("finances.page.table.status.pendingSepa")
      } else {
        return word("finances.page.table.status.pendingByCard")
      }

    return wordStatus[this.status]
  }
}
